/**
 * useFontLoader.ts
 * Hook React pour charger automatiquement les polices utilisées dans les shapes
 * Résout le problème des "polices fantômes" (polices non chargées avant utilisation)
 */
import { useEffect, useRef } from 'react';
import { useAppStore } from '../services/StateService';
import { FontManager } from '../FontManager';
import { logger } from '../utils/ProfessionalLogger';

/**
 * Hook pour charger automatiquement les polices utilisées dans les shapes
 * Surveille les changements de fontFamily et charge les polices à la demande
 */
export function useFontLoader(): void {
  const shapes = useAppStore(state => state.shapes);
  const loadedFontsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const fontManager = FontManager.getInstance();

    // Extraire toutes les fontFamily uniques des shapes
    const fontFamilies = new Set<string>();
    shapes.forEach(shape => {
      if (shape.type === 'text' && shape.fontFamily) {
        fontFamilies.add(shape.fontFamily);
      }
    });

    // Charger chaque police qui n'a pas encore été chargée
    fontFamilies.forEach(fontFamily => {
      if (!loadedFontsRef.current.has(fontFamily)) {
        // Vérifier si la police est déjà chargée
        if (!fontManager.isFontLoaded(fontFamily)) {
          logger.debug(`📝 Chargement de la police: ${fontFamily}`);

          // Charger la police à la demande
          fontManager
            .loadFontOnDemand(fontFamily)
            .then(success => {
              if (success) {
                loadedFontsRef.current.add(fontFamily);
                logger.info(`✅ Police chargée avec succès: ${fontFamily}`);
              } else {
                logger.warn(`⚠️ Échec du chargement de la police: ${fontFamily}`);
              }
            })
            .catch(error => {
              logger.error(`❌ Erreur lors du chargement de la police ${fontFamily}:`, error);
            });
        } else {
          // La police est déjà chargée, l'ajouter à la liste
          loadedFontsRef.current.add(fontFamily);
        }
      }
    });

    // Nettoyer les polices qui ne sont plus utilisées (optionnel, pour économiser la mémoire)
    // Pour l'instant, on garde toutes les polices chargées
  }, [shapes]);
}
